import * as Sentry from "@sentry/react";

type SentryFrameLike = {
  filename?: string;
  function?: string;
  module?: string;
  in_app?: boolean;
  lineno?: number;
  colno?: number;
};

type SentryExceptionLike = {
  type?: string;
  value?: string;
  stacktrace?: { frames?: SentryFrameLike[] };
};

type SentryBreadcrumbLike = {
  category?: string;
  message?: string;
  data?: Record<string, unknown>;
};

type SentryEventLike = {
  message?: string;
  request?: { url?: string };
  exception?: { values?: SentryExceptionLike[] };
  breadcrumbs?: SentryBreadcrumbLike[];
};

const THIRD_PARTY_FRAME_PATTERNS = [
  /(^|:\/\/)([^/]+\.)?tawk\.to\//i,
  /(^|:\/\/)va\.tawk\.to\//i,
  /(^|:\/\/)embed\.tawk\.to\//i,
  /(^|:\/\/)www\.googletagmanager\.com\//i,
  /(^|:\/\/)www\.google-analytics\.com\//i,
  /(^|:\/\/)analytics\.google\.com\//i,
  /(^|:\/\/)stats\.g\.doubleclick\.net\//i,
  /(^|:\/\/)td\.doubleclick\.net\//i,
  /(^|:\/\/)googleads\.g\.doubleclick\.net\//i,
  /(^|:\/\/)www\.googleadservices\.com\//i,
  /(^|:\/\/)connect\.facebook\.net\//i,
  /(^|:\/\/)([^/]+\.)?taboola\.com\//i,
  /(^|:\/\/)([^/]+\.)?app-us1\.com\//i,
  /(^|:\/\/)cloudflareinsights\.com\//i,
  /(^|:\/\/)challenges\.cloudflare\.com\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-extension:\/\//i,
];

const EXTENSION_BREADCRUMB_PATTERNS = [
  /\bbackpack:/i,
  /FromContentScriptTransportSender/i,
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari-extension:\/\//i,
];

const NOISY_ERROR_PATTERNS = [
  // Tawk.to live chat widget internals.
  /Socket server did not execute the callback for setAttributes/i,
  /Cannot read properties of undefined \(reading 'clientHeight'\)/i,
  /can't access property "clientHeight"/i,
  /\$_Tawk\.i18next/i,

  // Third-party tags and browser extensions.
  /Refused to evaluate a string as JavaScript because 'unsafe-eval'/i,
  /Unable to store cookie/i,
  /^\$ is not defined$/i,
  /^Sel is not defined$/i,
  /^Sel is undefined$/i,

  // Stale chunk after a fresh deploy.
  /Importing a module script failed/i,
  /Failed to fetch dynamically imported module/i,
];

function textOfEvent(event: SentryEventLike): string {
  const exceptionText = event.exception?.values
    ?.map((value) => `${value.type || ""}: ${value.value || ""}`)
    .join("\n") || "";

  return [event.message, exceptionText].filter(Boolean).join("\n");
}

function getFrames(event: SentryEventLike): SentryFrameLike[] {
  return event.exception?.values?.flatMap((value) => value.stacktrace?.frames || []) || [];
}

function isThirdPartyFrame(frame: SentryFrameLike): boolean {
  const filename = frame.filename || "";
  return THIRD_PARTY_FRAME_PATTERNS.some((pattern) => pattern.test(filename));
}

function isVendorOnlyFrame(frame: SentryFrameLike): boolean {
  const filename = frame.filename || "";
  return (
    /\/assets\/vendor-/i.test(filename) ||
    /\/assets\/sentry-/i.test(filename) ||
    /node_modules/i.test(filename) ||
    /\/@sentry\//i.test(filename)
  );
}

function hasAppOwnedFrame(frames: SentryFrameLike[]): boolean {
  return frames.some((frame) => {
    const filename = frame.filename || "";
    if (!filename || filename === "undefined" || filename === "<anonymous>") return false;
    if (isThirdPartyFrame(frame)) return false;
    if (isVendorOnlyFrame(frame)) return false;

    return (
      frame.in_app === true ||
      filename.startsWith("/") ||
      filename.includes("/assets/") ||
      filename.includes("thailand-elite.com") ||
      filename.includes("thailand-elite-portal-dev.pages.dev") ||
      filename.includes("thailandelite.fr")
    );
  });
}

function hasExtensionBreadcrumb(event: SentryEventLike): boolean {
  return (event.breadcrumbs || []).some((crumb) => {
    const dataText = crumb.data ? JSON.stringify(crumb.data) : "";
    const text = [crumb.category, crumb.message, dataText].filter(Boolean).join(" ");
    return EXTENSION_BREADCRUMB_PATTERNS.some((pattern) => pattern.test(text));
  });
}

function isKnownGoogleTranslateOverflow(event: SentryEventLike): boolean {
  const values = event.exception?.values;
  if (values?.length !== 1) return false;
  const exception = values[0];
  if (exception.type !== "RangeError" || exception.value !== "Maximum call stack size exceeded.") return false;

  // Google Translate TE_20260920, el_main build 6Yvjr9bd6SM.O. Verified against
  // the upstream script and Sentry issues 4A/4C (see docs/sentry-review-2026-09-23.md).
  // iOS attributes injected translation code to the document URL, so denyUrls
  // cannot identify it. Match the complete known stack signature, not all
  // document-URL errors, translated pages, or iOS stack overflows.
  const signature = [
    { function: "Ok", lineno: 226, colno: 63 },
    { function: "Qk", lineno: 226, colno: 408 },
    { function: "Qk", lineno: 226, colno: 382 },
    { lineno: 198, colno: 237 },
    { lineno: 190, colno: 41 },
    { lineno: 197, colno: 363 },
    { lineno: 190, colno: 70 },
  ];
  const frames = exception.stacktrace?.frames || [];
  const matches = (frame: SentryFrameLike, expected: SentryFrameLike) =>
    frame.lineno === expected.lineno && frame.colno === expected.colno &&
    (expected.function ? frame.function === expected.function :
      !frame.function || frame.function === "?" || frame.function === "<anonymous>");

  const isDocumentFrame = (frame: SentryFrameLike) => {
    const filename = frame.filename || "";
    if (!filename.startsWith("/") && !/^https?:\/\//i.test(filename)) return false;
    try {
      const url = new URL(filename, "https://thailand-elite.com");
      return ["thailand-elite.com", "www.thailand-elite.com", "thailandelite.fr", "www.thailandelite.fr"].includes(url.hostname) &&
        !url.pathname.startsWith("/assets/") && !url.pathname.startsWith("/src/") &&
        !url.pathname.split("/").pop()?.includes(".");
    } catch {
      return false;
    }
  };

  // Fail open if Google changes its build, or if any app/unknown frame is mixed in.
  return signature.every((expected) => frames.some((frame) => matches(frame, expected))) &&
    frames.every((frame) => isDocumentFrame(frame) && signature.some((expected) => matches(frame, expected)));
}

export function shouldDropSentryEvent(event: SentryEventLike): boolean {
  const text = textOfEvent(event);
  const frames = getFrames(event);
  const hasOwnedFrame = hasAppOwnedFrame(frames);

  if (isKnownGoogleTranslateOverflow(event)) return true;

  if (NOISY_ERROR_PATTERNS.some((pattern) => pattern.test(text))) {
    return true;
  }

  if (frames.length > 0 && frames.every((frame) => isThirdPartyFrame(frame))) {
    return true;
  }

  if (/\[Cloudflare Turnstile\] Error: \d+/i.test(text) && !hasOwnedFrame) {
    return true;
  }

  if (hasExtensionBreadcrumb(event) && !hasOwnedFrame) {
    return true;
  }

  if (/Maximum call stack size exceeded/i.test(text) && !hasOwnedFrame) {
    return true;
  }

  if (/can't access property "data", .* is undefined/i.test(text) && !hasOwnedFrame) {
    return true;
  }

  if (/Cannot read properties of undefined \(reading 'data'\)/i.test(text) && !hasOwnedFrame) {
    return true;
  }

  if (/Element type is invalid.*Lazy element type must resolve/i.test(text) && !hasOwnedFrame) {
    return true;
  }

  return false;
}

/**
 * Initialize Sentry error monitoring.
 * DSN is loaded from VITE_SENTRY_DSN env var.
 * Only active in production (skipped in dev to avoid noise).
 */
export function initSentry() {
  if (Sentry.getClient()) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    if (import.meta.env.PROD) console.error("Error monitoring is disabled: VITE_SENTRY_DSN was not provided at build time.");
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE, // "production" | "development"
    release: import.meta.env.VITE_APP_VERSION || "te-portal@1.0.0",

    // Capture 10% of sessions as performance traces in prod
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session replay: disabled (re-enable when needed for debugging)
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.0,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask PII in replays
        maskAllText: false,
        maskAllInputs: true, // Always mask form inputs (passwords, personal data)
        blockAllMedia: false,
      }),
    ],

    // Filter out noise
    ignoreErrors: [
      // Browser extensions
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      // Safari quirks
      "Non-Error promise rejection captured",
      // Supabase realtime reconnects (expected)
      /WebSocket.*closed/,
      "websocket error",
      // Chrome extension messages
      "A listener indicated an asynchronous response by returning true",
      // Supabase Auth JS v2 — Web Locks: fired when a new navigation steals the
      // auth lock from a previous tab/page. Expected browser behaviour on mobile
      // (especially on Google Ads landing page navigations). Not actionable.
      "Lock broken by another request with the 'steal' option",
      /AbortError.*steal/,
      /Lock broken by another request/,
      // Tawk.to live chat widget — internal bug in their minified code.
      // We never expose i18next on window.Tawk_API, so this is on their side.
      /\$_Tawk\.i18next/,
      // Third-party Web Audio BufferLoader (no BufferLoader in our code —
      // injected by GTM tag / browser extension with a bad URL).
      /BufferLoader.*XHR error/,
      // Stale chunk after a fresh deploy: old client tries to lazy-load a hash
      // that no longer exists. Reloading the page resolves it; not actionable.
      /Importing a module script failed/,
      /Failed to fetch dynamically imported module/,
      // Media resource errors from 3rd-party widgets (Tawk messages, browser
      // extensions). We render no <video>/<audio> in our app code.
      "The media resource indicated by the src attribute or assigned media provider object was not suitable",
      "NotSupportedError",
      // Tawk.to and injected third-party/extension scripts.
      /Socket server did not execute the callback for setAttributes/,
      /Cannot read properties of undefined \(reading 'clientHeight'\)/,
      /Refused to evaluate a string as JavaScript because 'unsafe-eval'/,
      /Unable to store cookie/,
      /^\$ is not defined$/,
      /^Sel is (not defined|undefined)$/,
    ],

    denyUrls: [
      /(^|:\/\/)([^/]+\.)?tawk\.to\//i,
      /(^|:\/\/)www\.googletagmanager\.com\//i,
      /(^|:\/\/)www\.google-analytics\.com\//i,
      /(^|:\/\/)connect\.facebook\.net\//i,
      /(^|:\/\/)([^/]+\.)?taboola\.com\//i,
      /(^|:\/\/)([^/]+\.)?app-us1\.com\//i,
      /(^|:\/\/)challenges\.cloudflare\.com\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
    ],

    beforeSend(event) {
      // Don't send events from localhost
      if (window.location.hostname === "localhost") return null;
      if (shouldDropSentryEvent(event as SentryEventLike)) return null;
      // Scrub auth tokens from captured URLs. Magic-link / ghost-login flows
      // put tokens in the URL hash (#access_token=...) and verify links carry
      // ?token=... — neither must ever reach Sentry.
      const scrubUrl = (url: unknown) =>
        typeof url === "string"
          ? url.replace(/#.*$/, "").replace(/([?&]token=)[^&]+/gi, "$1[redacted]")
          : url;
      if (event.request?.url) event.request.url = scrubUrl(event.request.url) as string;
      for (const b of event.breadcrumbs ?? []) {
        if (b.data?.url) b.data.url = scrubUrl(b.data.url);
        if (b.data?.to) b.data.to = scrubUrl(b.data.to);
        if (b.data?.from) b.data.from = scrubUrl(b.data.from);
      }
      return event;
    },
  });
}

// Re-export for use in components
export { Sentry };
